import * as Sentry from '@sentry/vue';
import {
  recordOPFSWriteFailure,
  recordOPFSBackoff,
  recordOrphanScrub,
} from '../session-stats';

const CHUNK = 1 * 1024 * 1024; // 1 MB chunks for chunked AES-GCM

// Write failures used to be terminal for the session. Now they back off and retry:
// a full disk that frees up, or a one-off error, shouldn't permanently disable
// spooling. Quota errors get the long backoff since they rarely clear quickly.
const BACKOFF_MS = [2_000, 10_000, 60_000];
const QUOTA_BACKOFF_MS = 60_000;

let sessionDir: FileSystemDirectoryHandle | null = null;
let sessionDirName: string | null = null;
let sessionKey: CryptoKey | null = null;
let initialized = false;
let consecutiveFailures = 0;
let disabledUntil = 0;
let sentryLogged = false;

function isQuotaError(err: unknown): boolean {
  return (
    err instanceof DOMException &&
    (err.name === 'QuotaExceededError' || err.code === 22)
  );
}

function noteWriteFailure(err: unknown): void {
  const kind = isQuotaError(err) ? 'quota' : isLockError(err) ? 'lock' : 'other';
  recordOPFSWriteFailure(kind);
  consecutiveFailures += 1;
  const backoff = isQuotaError(err)
    ? QUOTA_BACKOFF_MS
    : BACKOFF_MS[Math.min(consecutiveFailures - 1, BACKOFF_MS.length - 1)];
  disabledUntil = Date.now() + backoff;
  recordOPFSBackoff();
}

function noteWriteSuccess(): void {
  consecutiveFailures = 0;
  disabledUntil = 0;
}

// Serializes writes per file so two concurrent writeBytes() for the same key
// can't both hold an OPFS writable open at once (the second createWritable would
// throw NoModificationAllowedError). Keyed by sanitized filename, since distinct
// fileKeys can collide to the same file.
const writeChains = new Map<string, Promise<unknown>>();

function withFileLock<T>(fileName: string, fn: () => Promise<T>): Promise<T> {
  const prev = writeChains.get(fileName) ?? Promise.resolve();
  // Swallow the predecessor's result/error so one failed write can't reject the
  // whole chain for unrelated callers.
  const next = prev.then(() => fn(), () => fn());
  writeChains.set(fileName, next);
  // Drop the entry once this is the tail, so the map doesn't grow unbounded.
  void next.catch(() => {}).finally(() => {
    if (writeChains.get(fileName) === next) writeChains.delete(fileName);
  });
  return next;
}

function isLockError(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'NoModificationAllowedError';
}

async function isOPFSAvailable(): Promise<boolean> {
  try {
    if (typeof navigator?.storage?.getDirectory !== 'function') return false;
    const root = await navigator.storage.getDirectory();
    const h = await root.getFileHandle('_opfs_test', { create: true });
    const w = await h.createWritable();
    await w.close();
    await root.removeEntry('_opfs_test');
    return true;
  } catch {
    return false;
  }
}

/**
 * Hold a Web Lock named after our session dir for as long as this page lives.
 *
 * Web Locks are origin-scoped and released when the holding context goes away —
 * including when the tab is killed for running out of memory, which is exactly the
 * case that leaves a directory behind. An acquirable lock therefore proves no live
 * tab owns that directory.
 *
 * The outer request() promise never settles by design; we resolve as soon as the
 * lock is granted and let it be held for the session.
 */
function holdSessionLock(name: string): Promise<void> {
  if (!navigator.locks?.request) return Promise.resolve();
  return new Promise<void>((granted) => {
    navigator.locks
      .request(name, () => new Promise<never>(() => granted()))
      .catch(() => granted());
  });
}

/**
 * Delete session directories nobody owns.
 *
 * `cleanupOPFSSession` runs on pagehide/beforeunload, neither of which fires when
 * a tab is killed for memory. The sessionStorage key it left behind is gone too as
 * soon as the user opens a fresh tab. So without this, every crash strands its
 * spooled data in OPFS permanently, eating into the quota of every later session —
 * which is very likely why storage ran out well before its reported limit.
 */
async function scrubOrphanSessions(
  root: FileSystemDirectoryHandle,
  selfName: string
): Promise<void> {
  const entries = (root as any).entries?.();
  if (!entries || !navigator.locks?.request) return;

  let removed = 0;
  try {
    for await (const [name, handle] of entries) {
      if (
        handle.kind !== 'directory' ||
        !name.startsWith('session-') ||
        name === selfName
      ) {
        continue;
      }
      // Delete inside the lock callback so a tab can't claim the directory
      // between the check and the removal.
      await navigator.locks.request(name, { ifAvailable: true }, async (lock) => {
        if (!lock) return; // a live tab owns it
        await root.removeEntry(name, { recursive: true }).catch(() => {});
        removed += 1;
      });
    }
  } catch {
    // best-effort; a failed scrub must not block the session
  }
  if (removed > 0) recordOrphanScrub(removed);
}

export async function initOPFSSession(): Promise<boolean> {
  if (!(await isOPFSAvailable())) return false;

  try {
    const root = await navigator.storage.getDirectory();

    // Same-tab reload: our own previous dir, named in sessionStorage. Covered by
    // the lock-based scrub too, but this also handles browsers without Web Locks.
    const staleKey = sessionStorage.getItem('opfs-session-dir');
    if (staleKey) {
      await root.removeEntry(staleKey, { recursive: true }).catch(() => {});
      sessionStorage.removeItem('opfs-session-dir');
    }

    const id = `session-${crypto.randomUUID()}`;
    // Claim the name before creating the directory, so a concurrent scrub in
    // another tab can never see it unlocked.
    await holdSessionLock(id);
    sessionDir = await root.getDirectoryHandle(id, { create: true });
    sessionDirName = id;
    sessionKey = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      false, // non-extractable: key never leaves JS heap
      ['encrypt', 'decrypt']
    );
    sessionStorage.setItem('opfs-session-dir', id);
    initialized = true;
    consecutiveFailures = 0;
    disabledUntil = 0;
    sentryLogged = false; // fresh session may report a new write failure

    // Reclaim anything left by a crashed session. After our own dir exists, so it
    // is never a candidate.
    await scrubOrphanSessions(root, id);
    return true;
  } catch (err) {
    Sentry.captureException(err, { tags: { source: 'opfs-init' } });
    return false;
  }
}

function sanitizeKey(key: string): string {
  return key.replace(/\W/g, '_');
}

async function writeEncryptedChunked(
  key: CryptoKey,
  data: ArrayBuffer,
  writable: FileSystemWritableFileStream
): Promise<void> {
  const src = new Uint8Array(data);
  // 4-byte header: total unencrypted length for pre-allocation on read
  await writable.write(new Uint32Array([src.byteLength]).buffer as ArrayBuffer);
  for (let offset = 0; offset < src.byteLength; offset += CHUNK) {
    const chunk = src.subarray(offset, offset + CHUNK); // view, no copy
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, chunk);
    await writable.write(iv.buffer as ArrayBuffer); // 12 bytes
    await writable.write(new Uint32Array([ciphertext.byteLength]).buffer as ArrayBuffer); // 4 bytes
    await writable.write(ciphertext);
  }
  // Caller commits (close) on success or aborts on failure — see attemptWrite.
}

/**
 * One write attempt: open a writable, stream the encrypted payload, and commit.
 * On any failure we abort() so the partial swap file is discarded and — critically
 * — the file's write lock is released, so a retry (or the next write) doesn't hit
 * NoModificationAllowedError from a stranded lock.
 */
async function attemptWrite(
  dir: FileSystemDirectoryHandle,
  key: CryptoKey,
  fileName: string,
  plaintext: ArrayBuffer
): Promise<void> {
  const handle = await dir.getFileHandle(fileName, { create: true });
  const writable = await handle.createWritable();
  let committed = false;
  try {
    await writeEncryptedChunked(key, plaintext, writable);
    await writable.close();
    committed = true;
  } finally {
    if (!committed) await writable.abort().catch(() => {});
  }
}

async function readDecryptedChunked(
  key: CryptoKey,
  encrypted: ArrayBuffer
): Promise<ArrayBuffer> {
  const view = new DataView(encrypted);
  const totalSize = view.getUint32(0, true);
  const out = new Uint8Array(totalSize);
  let pos = 4;
  let outOffset = 0;
  while (pos < encrypted.byteLength) {
    const iv = new Uint8Array(encrypted, pos, 12); pos += 12;
    const chunkLen = view.getUint32(pos, true); pos += 4;
    const ciphertext = new Uint8Array(encrypted, pos, chunkLen); pos += chunkLen;
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
    out.set(new Uint8Array(plain), outOffset);
    outOffset += plain.byteLength;
  }
  return out.buffer;
}

export async function writeBytes(fileKey: string, plaintext: ArrayBuffer): Promise<boolean> {
  if (!isHealthy()) return false;
  const fileName = `${sanitizeKey(fileKey)}.enc`;

  // Serialize on the filename so a concurrent write to the same file waits rather
  // than racing for the writable lock.
  return withFileLock(fileName, async () => {
    // Re-check after the wait: a queued write may have tripped the backoff, or the
    // session may have been torn down while we sat in the chain.
    const dir = sessionDir;
    const key = sessionKey;
    if (!isHealthy() || !dir || !key) return false;
    try {
      const est = await navigator.storage.estimate();
      if (est.quota && est.usage && (est.quota - est.usage) < plaintext.byteLength * 2) {
        // Reported quota, not actual free disk — a soft signal, so record it but
        // don't back off: a later, smaller write may still fit.
        recordOPFSWriteFailure('quota');
        return false;
      }

      try {
        await attemptWrite(dir, key, fileName, plaintext);
      } catch (err) {
        // NoModificationAllowedError is usually transient: brief write-lock
        // contention or a swap-file allocation that momentarily failed. Yield and
        // retry once before backing off.
        if (!isLockError(err)) throw err;
        await new Promise((resolve) => setTimeout(resolve, 50));
        await attemptWrite(dir, key, fileName, plaintext);
      }
      noteWriteSuccess();
      return true;
    } catch (err) {
      noteWriteFailure(err);
      if (!sentryLogged) {
        sentryLogged = true;
        Sentry.captureException(err, { tags: { source: 'opfs-write' } });
      }
      return false;
    }
  });
}

export async function readBytes(fileKey: string): Promise<ArrayBuffer | null> {
  // Deliberately NOT gated on isHealthy(). A write failure pauses further writes,
  // but whatever already made it to disk is still valid ciphertext and the key and
  // directory are still live. Refusing to read it back would strand spooled output
  // that has no other copy.
  if (!sessionDir || !sessionKey) return null;
  try {
    const handle = await sessionDir.getFileHandle(`${sanitizeKey(fileKey)}.enc`);
    const encrypted = await (await handle.getFile()).arrayBuffer();
    return await readDecryptedChunked(sessionKey, encrypted);
  } catch {
    return null;
  }
}

export async function deleteBytes(fileKey: string): Promise<void> {
  if (!sessionDir) return;
  try {
    await sessionDir.removeEntry(`${sanitizeKey(fileKey)}.enc`);
  } catch {
    // non-fatal
  }
}

export async function cleanupOPFSSession(): Promise<void> {
  if (!sessionDirName) return;
  const root = await navigator.storage.getDirectory().catch(() => null);
  if (root) await root.removeEntry(sessionDirName, { recursive: true }).catch(() => {});
  sessionStorage.removeItem('opfs-session-dir');
  writeChains.clear();
  sessionDir = null;
  sessionKey = null;
  initialized = false;
}

/** Writable right now: initialized, and not inside a post-failure backoff window. */
export function isHealthy(): boolean {
  if (!initialized || !sessionDir || !sessionKey) return false;
  return Date.now() >= disabledUntil;
}

export function getOPFSHealth(): {
  initialized: boolean;
  consecutiveFailures: number;
  backoffMsRemaining: number;
} {
  return {
    initialized,
    consecutiveFailures,
    backoffMsRemaining: Math.max(0, disabledUntil - Date.now()),
  };
}
